import { useMatches } from 'react-router';
import { IAssistantPageContext } from 'shared';

import { useFetchContactQuery } from '../services/api/contactsApi/contact';
import { useFetchInvoiceQuery } from '../services/api/invoiceApi/invoice';
import { useFetchPoliciesQuery } from '../services/api/policyApi/policy';

import { useGetSelectedOrganisation } from './useGetSelectedOrganisation';

export interface AssistantPageContext extends IAssistantPageContext {
  /** e.g. `Policy "5% late fee"`; falls back to "this policy" while the page data loads */
  label: string;
}

/**
 * The policy, customer or invoice page the user is on, for the assistant drawer (rendered at the
 * root, so route params come from the deepest match). Labels reuse the pages' own cached queries.
 */
export function useAssistantPageContext(): AssistantPageContext | null {
  const matches = useMatches();
  const organisationId = useGetSelectedOrganisation()?.id as string;
  const { policyId, invoiceId, contactId } = matches[matches.length - 1]?.params ?? {};
  // The most specific record wins, e.g. a policy opened from a contact's page
  const type = policyId ? `policy` : invoiceId ? `invoice` : contactId ? `contact` : null;

  const { data: policies } = useFetchPoliciesQuery(organisationId, { skip: !organisationId || type !== `policy` });
  const { data: invoice } = useFetchInvoiceQuery({ organisationId, invoiceId: invoiceId as string }, { skip: !organisationId || type !== `invoice` });
  const { data: contact } = useFetchContactQuery({ organisationId, contactId: Number(contactId) }, { skip: !organisationId || type !== `contact` });

  if (type === `policy`) {
    const title = policies?.policies.find(item => item.policy.id === Number(policyId))?.policy.title;

    return { type, id: policyId as string, label: title ? `Policy "${title}"` : `this policy` };
  }

  if (type === `invoice`) {
    const number = invoice?.invoice?.xero_number as string | undefined;

    return { type, id: invoiceId as string, label: number ? `Invoice ${number}` : `this invoice` };
  }

  if (type === `contact`) {
    return { type, id: contactId as string, label: contact?.name ? `Customer "${contact.name}"` : `this customer` };
  }

  return null;
}
